import { useMutation } from '@tanstack/react-query'
import { ArrowRight, Bot, Send, User } from 'lucide-react'
import type { SubmitEvent } from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import type { TenantResponse } from '@/api/responses'
import { Alert, Badge, Button, Input, Spinner, SpinnerIcon } from '@/components/ui'
import { cn } from '@/lib/utils'

import { runPlayground } from '../api'
import type { PlaygroundResult } from '../types'
import { useAdvanceOnboarding, useTenantAgents } from '../useOnboarding'

export interface PlaygroundStepProps {
  readonly tenant: TenantResponse
  /** Avanca para "Conectar WhatsApp" (acao explicita do usuario apos experimentar o agente). */
  readonly onContinue: () => void
}

interface ChatTurn {
  readonly id: number
  readonly role: 'user' | 'agent'
  readonly text: string
}

/** As sugestoes de exemplo do estado vazio (empty state = action state, docs/design/01 secao 7). */
const SUGGESTION_KEYS = [
  'onboarding.playground.suggestions.first',
  'onboarding.playground.suggestions.second',
  'onboarding.playground.suggestions.third',
] as const

/**
 * Passo "Experimente seu agente": o usuario conversa com o agente provisional pelo endpoint de
 * Playground (ObserveOnly — nada e enviado a clientes reais), a primeira experiencia util e a meta
 * do onboarding (&lt; 10 min, ADR-015). Ao obter a primeira resposta util, marca
 * `OnboardingSession.FirstValueReachedAt`. O AgentMode fica sempre visivel, sem "AI theater".
 */
export function PlaygroundStep({ tenant, onContinue }: PlaygroundStepProps) {
  const { t } = useTranslation()
  const agentsQuery = useTenantAgents(tenant.id)
  const advance = useAdvanceOnboarding(tenant.id)

  const [turns, setTurns] = useState<readonly ChatTurn[]>([])
  const [draft, setDraft] = useState('')
  const [firstValueSent, setFirstValueSent] = useState(false)
  const nextTurnId = useRef(0)

  const agent = agentsQuery.data?.at(0)

  const mutation = useMutation({
    mutationFn: (variables: { message: string; userTurnId: number }): Promise<PlaygroundResult> => {
      if (agent === undefined) {
        return Promise.reject(new Error('agent-unavailable'))
      }
      return runPlayground(tenant.id, agent.id, variables.message)
    },
    onSuccess: (result) => {
      // A fala do usuario ja foi ecoada otimisticamente em `send`; aqui so anexamos a resposta do agente.
      const reply = result.decision.responseText ?? t('onboarding.playground.handoffNote')
      setTurns((current) => [...current, { id: nextTurnId.current++, role: 'agent', text: reply }])

      // Primeira resposta util: registra o instante da primeira experiencia de valor (uma unica vez).
      // Handoff (sem texto de resposta) NAO conta como primeira experiencia de valor (metrica ADR-015).
      if (!firstValueSent && result.decision.responseText !== null) {
        setFirstValueSent(true)
        advance.mutate({ firstValueReached: true })
      }
    },
    onError: (_error, variables) => {
      // Falha (inclui provider indisponivel): desfaz o eco otimista e devolve o texto ao input para
      // reenviar — o usuario nunca perde o que digitou.
      setTurns((current) => current.filter((turn) => turn.id !== variables.userTurnId))
      setDraft(variables.message)
    },
  })

  function send(message: string): void {
    const trimmed = message.trim()
    if (trimmed.length === 0 || agent === undefined || mutation.isPending) {
      return
    }

    // Eco otimista: a fala do usuario aparece imediatamente (loading distinto de "sem resposta"; na
    // 1a mensagem ja saimos do empty state para o "pensando").
    const userTurnId = nextTurnId.current++
    setTurns((current) => [...current, { id: userTurnId, role: 'user', text: trimmed }])
    setDraft('')
    mutation.mutate({ message: trimmed, userTurnId })
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()
    send(draft)
  }

  if (agentsQuery.isPending) {
    return (
      <div className="flex min-h-[420px] items-center justify-center p-10">
        <Spinner label={t('onboarding.playground.loadingAgent')} />
      </div>
    )
  }

  const providerUnavailable =
    mutation.error instanceof ApiError &&
    mutation.error.problem.errorCode === 'ai_provider_not_configured'

  return (
    <div className="flex flex-col gap-6 p-6 sm:p-10">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-action-primary">
            {t('onboarding.playground.eyebrow')}
          </span>
          <Badge variant="neutral">
            {t('onboarding.playground.agentModeLabel')}: {t('onboarding.playground.agentModeValue')}
          </Badge>
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground-primary sm:text-3xl">
          {t('onboarding.playground.title')}
        </h2>
        <p className="text-sm leading-relaxed text-foreground-secondary">
          {t('onboarding.playground.description')}
        </p>
      </div>

      {firstValueSent ? (
        <Alert variant="success" title={t('onboarding.playground.firstValueTitle')}>
          {t('onboarding.playground.firstValueDescription')}
        </Alert>
      ) : null}

      {providerUnavailable ? (
        <Alert variant="warning" title={t('onboarding.playground.providerUnavailableTitle')}>
          {t('onboarding.playground.providerUnavailableDescription')}
        </Alert>
      ) : mutation.isError ? (
        <Alert variant="danger" title={t('onboarding.playground.errorTitle')}>
          {t('onboarding.playground.errorDescription')}
        </Alert>
      ) : null}

      <div className="flex min-h-[220px] flex-col gap-3 rounded-xl border border-border-default bg-background-subtle p-4">
        {turns.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-6 text-center">
            <span
              aria-hidden="true"
              className="flex h-12 w-12 items-center justify-center rounded-2xl bg-background-elevated text-action-primary"
            >
              <Bot className="h-6 w-6" />
            </span>
            <p className="text-sm font-medium text-foreground-primary">
              {t('onboarding.playground.emptyTitle')}
            </p>
            <p className="max-w-sm text-xs text-foreground-muted">
              {t('onboarding.playground.emptyHint')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
              {SUGGESTION_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    send(t(key))
                  }}
                  disabled={agent === undefined || mutation.isPending}
                  className="rounded-full border border-border-default bg-background-elevated px-3 py-1.5 text-xs text-foreground-secondary transition-colors hover:border-action-primary hover:text-action-primary disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t(key)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <ol className="flex flex-col gap-3">
            {turns.map((turn) => (
              <li
                key={turn.id}
                className={cn(
                  'flex gap-2.5',
                  turn.role === 'user' ? 'flex-row-reverse' : 'flex-row',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                    turn.role === 'user'
                      ? 'bg-action-primary text-foreground-on-action'
                      : 'bg-background-elevated text-action-primary',
                  )}
                >
                  {turn.role === 'user' ? (
                    <User className="h-4 w-4" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}
                </span>
                <div
                  className={cn(
                    'max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
                    turn.role === 'user'
                      ? 'bg-action-primary text-foreground-on-action'
                      : 'bg-background-elevated text-foreground-primary',
                  )}
                >
                  <span className="sr-only">
                    {turn.role === 'user'
                      ? t('onboarding.playground.youLabel')
                      : t('onboarding.playground.agentLabel')}
                    :{' '}
                  </span>
                  {turn.text}
                </div>
              </li>
            ))}
            {mutation.isPending ? (
              <li className="flex items-center gap-2 text-xs text-foreground-muted">
                <SpinnerIcon size="sm" />
                {t('onboarding.playground.thinking')}
              </li>
            ) : null}
          </ol>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            id="playground-message"
            type="text"
            label={t('onboarding.playground.inputLabel')}
            placeholder={t('onboarding.playground.inputPlaceholder')}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
            }}
            disabled={agent === undefined}
          />
        </div>
        <Button
          type="submit"
          loading={mutation.isPending}
          disabled={agent === undefined || draft.trim().length === 0}
          aria-label={t('onboarding.playground.send')}
        >
          <Send aria-hidden="true" className="h-4 w-4" />
        </Button>
      </form>

      <p className="text-xs text-foreground-muted">{t('onboarding.playground.disclaimer')}</p>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-default pt-6">
        <p className="text-sm text-foreground-secondary">
          {t('onboarding.playground.continuePrompt')}
        </p>
        <Button variant="secondary" onClick={onContinue} className="gap-1.5">
          {t('onboarding.playground.continueCta')}
          <ArrowRight aria-hidden="true" className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
